import { createSignal, createMemo, type Accessor } from 'solid-js';
import type { SetStoreFunction } from 'solid-js/store';
import type { Canvas } from 'Shared/posters/project/posterSchema';
import type { Dimensions } from 'Shared/posters/types';

export interface CanvasContext {
	canvas: Accessor<Canvas>
	canvasBg: Accessor<string>
	setCanvasBg: (v: string) => void
	// Theme-variable bindings for canvas properties (currently just "background").
	canvasBindings: Accessor<Record<string, string>>
	setCanvasBindings: (v: Record<string, string>) => void
}

export function createCanvasState(
	canvasSlice: () => Canvas | undefined,
	setCanvas:   SetStoreFunction<Canvas> | undefined,
	dims:        () => Dimensions,
): CanvasContext {
	// Editable hosts pass the caller's live canvas slice plus a setter (writes go straight to the
	// caller's store); read-only hosts pass only a value and write nowhere, so their writes land in
	// local overrides instead. Reads follow the caller's slice in both cases until such an override
	// is set: seeding local state once would freeze the background of a long-lived viewer (the event
	// page's captured poster) against every later edit of the design it is showing.
	const [bgOverride, setBgOverride] = createSignal<string|undefined>()
	const [bindingsOverride, setBindingsOverride] = createSignal<Record<string, string>|undefined>()

	const canvasBg = setCanvas
		? () => canvasSlice()?.background ?? '#000'
		: () => bgOverride() ?? canvasSlice()?.background ?? '#000'
	const canvasBindings = setCanvas
		? () => canvasSlice()?.bindings ?? {}
		: () => bindingsOverride() ?? canvasSlice()?.bindings ?? {}
	const setCanvasBg = setCanvas ? (v: string) => setCanvas('background', v) : setBgOverride
	const setCanvasBindings = setCanvas
		? (v: Record<string, string>) => setCanvas('bindings', v)
		: (v: Record<string, string>) => setBindingsOverride(v)

	// The owning theme's dimensions are authoritative: they override the design's own
	// width/height so the poster always renders at the theme's size — even as those
	// dimensions arrive asynchronously or change while the builder stays mounted.
	const canvas = createMemo<Canvas>(() => ({ ...canvasSlice(), ...dims() }))

	return { canvas, canvasBg, setCanvasBg, canvasBindings, setCanvasBindings }
}

